// Block-based visual template editor.
//
// Composes the BlockPalette (left) and BlockCanvas (right). Owns no state of
// its own — drives the tree via props from the parent page, which is also
// responsible for save/preview/mode switching.

import { BlockTree, ScopeComponent, TemplateContextSchema } from '@/types';
import { appendBlock } from './block-tree';
import { BlockCanvas } from './BlockCanvas';
import { BlockPalette } from './BlockPalette';
import { VariableOption } from './InlineEditor';

interface BlockEditorProps {
  tree: BlockTree;
  onChange: (next: BlockTree) => void;
  scopeComponents: ScopeComponent[];
  contextSchema: TemplateContextSchema | null;
}

export function BlockEditor({
  tree,
  onChange,
  scopeComponents,
  contextSchema,
}: Readonly<BlockEditorProps>) {
  const variables = buildVariableOptions(contextSchema, scopeComponents);
  const filterOptions =
    contextSchema?.filters.map((f) => ({
      name: f.name,
      description: f.description,
    })) ?? [];

  const insert = (block: Parameters<typeof appendBlock>[1]) => {
    onChange(appendBlock(tree, block));
  };

  return (
    <div
      style={{
        display: 'flex',
        flex: 1,
        minHeight: 0,
        borderTop: '1px solid var(--mantine-color-default-border)',
      }}
    >
      <div
        style={{
          width: 240,
          flexShrink: 0,
          borderRight: '1px solid var(--mantine-color-default-border)',
          background: 'var(--mantine-color-default-hover)',
        }}
      >
        <BlockPalette scopeComponents={scopeComponents} onInsert={insert} />
      </div>
      <div style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
        <BlockCanvas
          tree={tree}
          onChange={onChange}
          scopeComponents={scopeComponents}
          variables={variables}
          filterOptions={filterOptions}
        />
      </div>
    </div>
  );
}

function buildVariableOptions(
  schema: TemplateContextSchema | null,
  scopeComponents: ScopeComponent[]
): VariableOption[] {
  if (!schema) {
    return [];
  }
  const out: VariableOption[] = [];
  for (const f of schema.global_fields) {
    out.push({
      path: f.name,
      label: humanise(f.name),
      group: 'Global fields',
      description: f.description,
    });
  }
  for (const f of schema.subject_fields) {
    out.push({
      path: `subject.${f.name}`,
      label: humanise(f.name),
      group: 'Subject fields',
      description: f.description,
    });
  }
  // Section refs aren't typically inserted inline, but expose them anyway for
  // power users who want `subject.sections['x']` inside prose.
  for (const sec of schema.available_sections) {
    const sc = scopeComponents.find((s) => s.slug === sec.slug);
    out.push({
      path: `subject.sections['${sec.slug}']`,
      label: sc?.name ?? sec.name,
      group: 'Sections',
      description: sec.description ?? '',
    });
  }
  return out;
}

function humanise(s: string): string {
  return s.replaceAll(/[_.]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
